import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

// A top-level location filter for users who can see everything (owner/admin).
// When set, location-aware pages scope their data to that location so the view
// isn't a confusing mix of every branch/warehouse/kitchen at once.
const LocationCtx = createContext(null);

export const LocationProvider = ({ children }) => {
  const { can } = useAuth();
  const canPick = can('locations:read');   // branch users are already scoped, so they don't pick
  const [locations, setLocations] = useState([]);
  const [picked, setPicked] = useState(() => localStorage.getItem('erp_loc_filter') || '');

  const setLocationId = (id) => {
    setPicked(id);
    if (id) localStorage.setItem('erp_loc_filter', id); else localStorage.removeItem('erp_loc_filter');
  };

  // Branch users can't see or change the top-bar picker and are already scoped by
  // the backend to their own branch. Never apply a leftover filter for them —
  // otherwise a location an admin picked earlier in this browser (stored in
  // localStorage) silently hides the branch user's own requests/returns.
  const locationId = canPick ? picked : '';

  // Loaded once per session, so Locations calls this after a save — otherwise a
  // branch added or renamed there stays missing from the picker until a reload.
  const reloadLocations = useCallback(() => {
    if (!canPick) return;
    api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => {});
  }, [canPick]);
  useEffect(reloadLocations, [reloadLocations]);

  return <LocationCtx.Provider value={{ canPick, locations, locationId, setLocationId, reloadLocations }}>{children}</LocationCtx.Provider>;
};

export const useLocationFilter = () => useContext(LocationCtx) || { canPick: false, locations: [], locationId: '', setLocationId: () => {}, reloadLocations: () => {} };
